import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COMPANY, SERVICES } from "@/data/mock";

const SERVICE_OPTIONS = [
  ...SERVICES.map((s) => ({ value: s.slug, label: s.title })),
  { value: "other", label: "Something else" }
];

const BUDGET_OPTIONS = [
  "Under £25k",
  "£25k – £75k",
  "£75k – £150k",
  "£150k – £400k",
  "£400k+"
];

const TIMELINE_OPTIONS = ["ASAP", "Within 3 months", "3 – 6 months", "6 – 12 months", "Just exploring"];

export default function Contact() {
  const [params] = useSearchParams();
  const initialService = params.get("service") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: initialService,
    budget: "",
    timeline: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please pick a service.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        budget: form.budget || null,
        timeline: form.timeline || null,
        message: form.message
      });
      setSubmitted(true);
      toast.success("Thanks — we’ll be in touch within one working day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        service: "",
        budget: "",
        timeline: "",
        message: ""
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-brand-bgSageSoft">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
        <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
          Get in touch
        </p>
        <h1 className="mt-4 font-display text-5xl md:text-7xl font-semibold tracking-tighter leading-[0.95] text-brand-ink max-w-3xl text-balance">
          Tell us about your project.
        </h1>
        <p className="mt-6 text-lg text-brand-inkSoft max-w-2xl">
          Free site visit, fixed-price written quote within a week. We aim to reply within one
          working day — usually a lot sooner.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 grid md:grid-cols-12 gap-10">
        {/* contact details */}
        <aside className="md:col-span-4 order-last md:order-first">
          <div className="rounded-3xl bg-brand-ink text-brand-bg p-8 sticky top-24">
            <h3 className="font-display text-2xl font-medium tracking-tight">Or reach us directly</h3>
            <ul className="mt-6 space-y-5">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-1 text-brand-ochre" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-bg/50">Phone</p>
                  <a
                    href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                    className="font-medium hover:text-brand-ochre"
                    data-testid="contact-phone"
                  >
                    {COMPANY.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-1 text-brand-ochre" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-bg/50">Email</p>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="font-medium hover:text-brand-ochre"
                    data-testid="contact-email"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-brand-ochre" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-bg/50">Yard</p>
                  <p className="font-medium">{COMPANY.address}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-1 text-brand-ochre" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-bg/50">Hours</p>
                  <p className="font-medium">{COMPANY.hours}</p>
                </div>
              </li>
            </ul>
            <div className="mt-8 pt-6 border-t border-white/10 text-sm text-brand-bg/70 leading-relaxed">
              We never share your details. One site manager will be in touch — not a call centre.
            </div>
          </div>
        </aside>

        {/* form */}
        <div className="md:col-span-8">
          {submitted ? (
            <div
              data-testid="contact-success"
              className="rounded-3xl border border-brand-line bg-white p-10 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-brand-bgAlt mx-auto inline-flex items-center justify-center text-brand-terracotta">
                <Send className="w-6 h-6" />
              </div>
              <h2 className="mt-6 font-display text-3xl font-medium tracking-tight text-brand-ink">
                Got it. Thanks.
              </h2>
              <p className="mt-3 text-brand-inkSoft max-w-md mx-auto">
                Your message is in. We’ll be in touch within one working day to arrange a free site
                visit.
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                data-testid="contact-send-another"
                className="mt-8 rounded-full"
              >
                Send another
              </Button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="rounded-3xl border border-brand-line bg-white p-7 md:p-10 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-brand-ink font-medium">Your name</Label>
                  <Input
                    id="name"
                    data-testid="quote-input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    required
                    minLength={2}
                    placeholder="Jane Doe"
                    className="mt-2 h-12 rounded-xl bg-brand-bg border-brand-line focus-visible:ring-brand-terracotta"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-brand-ink font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="quote-input-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    required
                    placeholder="jane@example.com"
                    className="mt-2 h-12 rounded-xl bg-brand-bg border-brand-line focus-visible:ring-brand-terracotta"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="phone" className="text-brand-ink font-medium">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="quote-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    required
                    placeholder="+44 7000 000000"
                    className="mt-2 h-12 rounded-xl bg-brand-bg border-brand-line focus-visible:ring-brand-terracotta"
                  />
                </div>
                <div>
                  <Label htmlFor="service" className="text-brand-ink font-medium">Service</Label>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger
                      id="service"
                      data-testid="quote-select-service"
                      className="mt-2 h-12 rounded-xl bg-brand-bg border-brand-line focus:ring-brand-terracotta"
                    >
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem
                          key={s.value}
                          value={s.value}
                          data-testid={`quote-service-${s.value}`}
                        >
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="budget" className="text-brand-ink font-medium">Budget (optional)</Label>
                  <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                    <SelectTrigger
                      id="budget"
                      data-testid="quote-select-budget"
                      className="mt-2 h-12 rounded-xl bg-brand-bg border-brand-line focus:ring-brand-terracotta"
                    >
                      <SelectValue placeholder="Rough range" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGET_OPTIONS.map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="timeline" className="text-brand-ink font-medium">Timeline (optional)</Label>
                  <Select value={form.timeline} onValueChange={(v) => update("timeline", v)}>
                    <SelectTrigger
                      id="timeline"
                      data-testid="quote-select-timeline"
                      className="mt-2 h-12 rounded-xl bg-brand-bg border-brand-line focus:ring-brand-terracotta"
                    >
                      <SelectValue placeholder="When would you like to start?" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMELINE_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-brand-ink font-medium">Tell us about the project</Label>
                <Textarea
                  id="message"
                  data-testid="quote-input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  required
                  minLength={10}
                  rows={6}
                  placeholder="Where is the property? What are you hoping to build / change? Any drawings or rough sizes?"
                  className="mt-2 rounded-xl bg-brand-bg border-brand-line focus-visible:ring-brand-terracotta"
                />
              </div>

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-xs text-brand-inkSoft">
                  By sending this you agree we can contact you about your enquiry.
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit"
                  className="rounded-full h-12 px-7 bg-brand-terracotta hover:bg-brand-terracottaDark text-white shadow-[0_12px_32px_rgba(192,86,64,0.25)]"
                >
                  {submitting ? "Sending…" : "Send enquiry"} <Send className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
